import { createAction } from "@reduxjs/toolkit";
import type { AlignmentOptions } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { ExportToPNGStatus, ScrollPosition } from "sequenceAnalyser/redux/reducers/sequenceAnalyserReducer.types";
import type { SequenceSearchMatch } from "sequenceAnalyser/utilities/search/search";
import type { SequenceSelection } from "sequenceAnalyser/utilities/selection/selection";
import type { RemovedSequence, SequenceAnalyserVisualisation } from "sequenceAnalyser/utilities/sequenceAnalyser.types";

export const createSelection = createAction("sequenceAnalyser/createSelection", (selection: SequenceSelection) => ({
  payload: {
    selection,
  },
}));

export const addIdsToSelection = createAction("sequenceAnalyser/addIdToSelection", (ids: number[]) => ({
  payload: {
    ids,
  },
}));

export const removeIdsFromSelection = createAction("sequenceAnalyser/removeIdFromSelection", (ids: number[]) => ({
  payload: {
    ids,
  },
}));

export const clearSelection = createAction("sequenceAnalyser/clearSelection", () => ({
  payload: {
    selection: {
      ids: [],
    },
  },
}));

export const zoomIn = createAction("sequenceAnalyser/zoomIn");
export const zoomOut = createAction("sequenceAnalyser/zoomOut");
export const resetZoom = createAction("sequenceAnalyser/resetZoom");

export const createSearchActiveMatch = createAction(
  "sequenceAnalyser/createSearchActiveMatch",
  (match: SequenceSearchMatch) => ({
    payload: {
      match,
    },
  })
);

export const updateHasColorsAndFeaturesVisible = createAction(
  "sequenceAnalyser/updateHasColorsAndFeaturesVisible",
  (hasColorsAndFeaturesVisible: boolean) => ({
    payload: {
      hasColorsAndFeaturesVisible,
    },
  })
);

export const updateShowHoveredResidueTooltipSetting = createAction(
  "sequenceAnalyser/updateShowHoveredResidueTooltipSetting",
  (show: boolean) => ({
    payload: {
      show,
    },
  })
);

export const updateShowSequenceNavigatorSetting = createAction(
  "sequenceAnalyser/updateShowSequenceNavigatorSetting",
  (show: boolean) => ({
    payload: {
      show,
    },
  })
);

export const updateShowSimilarityTreeSetting = createAction(
  "sequenceAnalyser/updateShowSimilarityTreeSetting",
  (show: boolean) => ({
    payload: {
      show,
    },
  })
);

export const updateVisualisationsSetting = createAction(
  "sequenceAnalyser/updateVisualisationsSetting",
  (visualisations: SequenceAnalyserVisualisation[]) => ({
    payload: {
      visualisations,
    },
  })
);

export const updateLabelCompositionSetting = createAction(
  "sequenceAnalyser/updateLabelCompositionSetting",
  (labelComposition: string[]) => ({
    payload: {
      labelComposition,
    },
  })
);

export const updateSidebarPixelWidth = createAction("sequenceAnalyser/updateSidebarPixelWidth", (width: number) => ({
  payload: {
    width,
  },
}));

export const updateScrollPosition = createAction(
  "sequenceAnalyser/updateScrollPosition",
  (scrollPosition?: ScrollPosition) => ({
    payload: {
      scrollPosition,
    },
  })
);

export const sequenceTableLoaded = createAction("sequenceAnalyser/sequenceTableLoaded");

export const removedSequences = createAction("sequenceAnalyser/removedSequences", (sequences: RemovedSequence[]) => ({
  payload: {
    sequences,
  },
}));

export const removedSequencesInserted = createAction(
  "sequenceAnalyser/removedSequencesInserted",
  (sequences: RemovedSequence[]) => ({
    payload: {
      sequences,
    },
  })
);

export const runAlignment = createAction("sequenceAnalyser/runAlignment", (alignmentOptions: AlignmentOptions) => ({
  payload: {
    alignmentOptions,
  },
}));

export const changeExportToPNGStatus = createAction(
  "sequenceAnalyser/changeExportToPNGStatus",
  (exportToPNGStatus: ExportToPNGStatus) => ({
    payload: {
      exportToPNGStatus,
    },
  })
);
